import axios from 'axios';
import API_CONFIG from '../config/api.config';

const CUSTOMER_TOKEN_KEY = 'customer_token';
const CUSTOMER_DATA_KEY = 'customer_data';
const CUSTOMER_LAST_ACTIVITY_KEY = 'customer_last_activity';
const SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour in milliseconds

// Helper functions
const updateLastActivity = () => {
  localStorage.setItem(CUSTOMER_LAST_ACTIVITY_KEY, Date.now().toString());
};

const isSessionExpired = () => {
  const lastActivity = localStorage.getItem(CUSTOMER_LAST_ACTIVITY_KEY);
  if (!lastActivity) {
    return true; // No activity recorded, consider expired
  }
  
  const lastActivityTime = parseInt(lastActivity, 10);
  const currentTime = Date.now();
  const timeSinceLastActivity = currentTime - lastActivityTime;
  
  return timeSinceLastActivity > SESSION_TIMEOUT_MS;
};

const logout = () => {
  localStorage.removeItem(CUSTOMER_TOKEN_KEY);
  localStorage.removeItem(CUSTOMER_DATA_KEY);
  localStorage.removeItem(CUSTOMER_LAST_ACTIVITY_KEY);
};

export const customerAuthService = {
  // Register new customer
  register: async (websitePath, customerData) => {
    try {
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/public/customer/${websitePath}/register`,
        customerData
      );
      
      if (response.data && response.data.accessToken) {
        // Auto login after registration
        localStorage.setItem(CUSTOMER_TOKEN_KEY, response.data.accessToken);
        localStorage.setItem(CUSTOMER_DATA_KEY, JSON.stringify(response.data.customer));
        updateLastActivity(); // Set initial activity timestamp
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Registration failed' };
    }
  },
  
  // Check availability of email/phone for a website
  checkAvailability: async (websitePath, { email, phone }) => {
    try {
      const response = await axios.get(
        `${API_CONFIG.BASE_URL}/public/customer/${websitePath}/availability`,
        { params: { email, phone } }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Availability check failed' };
    }
  },

  // Login customer
  login: async (websitePath, credentials) => {
    try {
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/public/customer/${websitePath}/login`,
        credentials
      );
      
      if (response.data && response.data.accessToken) {
        localStorage.setItem(CUSTOMER_TOKEN_KEY, response.data.accessToken);
        localStorage.setItem(CUSTOMER_DATA_KEY, JSON.stringify(response.data.customer));
        updateLastActivity(); // Set initial activity timestamp
      }
      
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Login failed' };
    }
  },

  // Logout customer
  logout: () => {
    logout();
  },

  // Update last activity timestamp
  updateLastActivity: () => {
    updateLastActivity();
  },

  // Check if session has expired due to inactivity
  isSessionExpired: () => {
    return isSessionExpired();
  },

  // Get current customer info (with session expiration check)
  getCurrentCustomer: () => {
    // Check if session expired
    if (isSessionExpired()) {
      console.log('[customerAuthService] Session expired due to inactivity, logging out...');
      logout();
      return null;
    }
    
    const customerData = localStorage.getItem(CUSTOMER_DATA_KEY);
    return customerData ? JSON.parse(customerData) : null;
  },

  // Check if customer is authenticated (with session expiration check)
  isAuthenticated: () => {
    // Check if session expired
    if (isSessionExpired()) {
      console.log('[customerAuthService] Session expired due to inactivity, logging out...');
      logout();
      return false;
    }
    
    return !!localStorage.getItem(CUSTOMER_TOKEN_KEY);
  },

  // Get auth token (with session expiration check)
  getToken: () => {
    // Check if session expired
    if (isSessionExpired()) {
      console.log('[customerAuthService] Session expired due to inactivity, logging out...');
      logout();
      return null;
    }
    
    return localStorage.getItem(CUSTOMER_TOKEN_KEY);
  }
};

// Axios interceptor to add customer token to public requests if available
axios.interceptors.request.use(
  (config) => {
    // Add token for public customer endpoints and ecommerce order endpoints
    if (config.url?.includes('/api/public/customer') || config.url?.includes('/api/public/website')) {
      // Exclude login and register endpoints from session expiration check
      // These are authentication endpoints where user is trying to log in
      const isAuthEndpoint = config.url?.includes('/login') || config.url?.includes('/register');
      
      // Only check session expiration for authenticated endpoints (not login/register)
      if (!isAuthEndpoint) {
        // Check if session expired before making request
        if (isSessionExpired()) {
          console.log('[customerAuthService] Session expired in interceptor, logging out...');
          logout();
          return Promise.reject(new Error('Session expired due to inactivity'));
        }
      }
      
      // Add token if available (for authenticated requests)
      const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
      if (token && !isAuthEndpoint) {
        config.headers['Authorization'] = `Bearer ${token}`;
        // Update activity on API call
        updateLastActivity();
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default customerAuthService;

